/**
 * The ONE definition of who or what an audit row is ABOUT (PB-4773).
 *
 * Three readers name the same rows: Auth's audit export (the CSV), Auth's
 * Identity Activity feed, and — once it reads `/v1/audit` — the Admin Console,
 * which names rows the platform returns identity-blind. Each gets the ids to
 * look up from here and composes the name from here, so a row about one person
 * or team cannot read two ways on two screens. Pure and dependency-free for
 * that reason; the lookups themselves stay with Auth, the identity authority.
 *
 * The rules are PB-5401's and PB-5435's, moved verbatim out of Auth's
 * `audit-search.service.ts`:
 *
 * - A `user` row, and a `local_account_provisioning` row, is about the person
 *   in `entityId`.
 * - A `team_member` or `team_membership_request` row names its person and its
 *   team in the payload (`userId`, `teamId`), and reads "Person (Team)", or
 *   whichever half resolved.
 * - A `team` row is about the team in `entityId`, named by its CURRENT name so
 *   every row about one team says the same thing, else by the name a creation
 *   or rename recorded.
 * - A `tenant_invitation` row is named by the address it recorded AT THE TIME.
 *   An accepted invitation's payload deliberately holds no address — the audit
 *   table is readable by every tenant administrator — so its honest subject is
 *   the redeeming member, `acceptedUserId`.
 *
 * A subject that cannot be named is `null`, never a placeholder: printing
 * something that looked like a name would be a fabrication in a compliance
 * record.
 */
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
/* The rows whose `entityId` is a person's user id. */
const PERSON_ENTITIES = new Set([
    "user",
    "local_account_provisioning",
]);
const TEAM_ENTITY = "team";
const TEAM_PERSON_ENTITIES = new Set([
    "team_member",
    "team_membership_request",
]);
const INVITATION_ENTITY = "tenant_invitation";
/** The user id this row is about, when it has one. */
export function auditSubjectUserId(row) {
    if (PERSON_ENTITIES.has(row.entityType)) {
        return UUID_RE.test(row.entityId) ? row.entityId : null;
    }
    if (TEAM_PERSON_ENTITIES.has(row.entityType)) {
        return payloadUuid(row.payload, "userId");
    }
    if (row.entityType === "hoshin_plan_membership") {
        return (payloadUuid(payloadRecord(row.payload).after, "userId") ??
            payloadUuid(payloadRecord(row.payload).before, "userId"));
    }
    if (row.entityType !== INVITATION_ENTITY)
        return null;
    if (payloadString(row.payload, "email"))
        return null;
    return payloadUuid(row.payload, "acceptedUserId");
}
/** The team this row is about, when it names one. */
export function auditSubjectTeamId(row) {
    if (row.entityType === TEAM_ENTITY) {
        return UUID_RE.test(row.entityId) ? row.entityId : null;
    }
    return TEAM_PERSON_ENTITIES.has(row.entityType)
        ? payloadUuid(row.payload, "teamId")
        : null;
}
/** Who or what the row is about, in words, or `null` when nothing names it. */
export function auditSubjectName(row, names) {
    if (PERSON_ENTITIES.has(row.entityType)) {
        return names.people.get(row.entityId) ?? null;
    }
    if (row.entityType === TEAM_ENTITY) {
        return names.teams.get(row.entityId) ?? payloadString(row.payload, "name");
    }
    if (TEAM_PERSON_ENTITIES.has(row.entityType)) {
        const personId = payloadUuid(row.payload, "userId");
        const teamId = payloadUuid(row.payload, "teamId");
        const person = personId ? (names.people.get(personId) ?? null) : null;
        const team = teamId ? (names.teams.get(teamId) ?? null) : null;
        if (person && team)
            return `${person} (${team})`;
        return person ?? team;
    }
    if (row.entityType === "hoshin_plan_membership") {
        const id = auditSubjectUserId(row);
        return id ? (names.people.get(id) ?? null) : null;
    }
    if (row.entityType !== INVITATION_ENTITY)
        return null;
    const invited = payloadString(row.payload, "email");
    if (invited !== null)
        return invited;
    const accepted = payloadUuid(row.payload, "acceptedUserId");
    return accepted === null ? null : (names.people.get(accepted) ?? null);
}
/* A payload id that is safe to look up: the lookups compare it with a uuid
   column, so anything else would be a cast error rather than no match. */
function payloadUuid(payload, key) {
    const value = payloadString(payload, key);
    return value !== null && UUID_RE.test(value) ? value : null;
}
/* One string field out of an audit payload, or null. The payload is `unknown`
   because whatever wrote the row chose its shape. A blank string is null too:
   an empty subject reads as a missing one, not as a name. */
function payloadString(payload, key) {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        return null;
    }
    const value = payload[key];
    if (typeof value !== "string")
        return null;
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
}
/** Read a membership snapshot only when its payload value is an object. */
function payloadRecord(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
//# sourceMappingURL=audit-subject.js.map